import React from "react";
import gear from "../data/gear.json";
import { AFFILIATE, affiliateUrl } from "../config/affiliates";
import Icon from "./Icon";

/**
 * A gear suggestion, shown only where the engine has computed a need.
 *
 * The whole design rests on one rule: this renders nothing unless the
 * recipe actually calls for filtration. No diagnosis, no link. That's what
 * separates it from an ad, and it's also the reason it should work — the
 * reader has just been told they're several stops over, with the specific
 * number, and the product is the answer to that sentence rather than an
 * interruption of it.
 *
 * Three things it deliberately doesn't do: load any third-party script or
 * pixel (plain tagged hrefs only, so the privacy page's promise about
 * third-party embeds stays true), hide the disclosure (it sits above the
 * links, not in a footer), or recommend a filter the engine didn't point
 * at (the kit shown is chosen by matching the calculated stops).
 */

function stopsOf(label) {
  const factor = parseFloat(String(label).replace(/[^0-9.]/g, ""));
  if (!factor || factor <= 1) return null;
  return Math.round(Math.log2(factor) * 10) / 10;
}

function pickKit(camera, recommendedFilter) {
  const kits = gear.ndKits[camera.id];
  if (kits && kits.length) {
    // Prefer the kit that actually contains the filter we recommended.
    const exact = kits.find((k) => (k.covers || []).includes(recommendedFilter));
    if (exact) return exact;

    // Otherwise the kit whose range reaches closest to what's needed —
    // relevant when the first-party set tops out short and a third-party
    // one goes further, which is a genuinely useful thing to surface.
    const needed = stopsOf(recommendedFilter);
    if (needed != null) {
      const ranked = kits
        .map((k) => ({ kit: k, max: Math.max(...(k.covers || []).map(stopsOf).filter(Boolean), 0) }))
        .filter((r) => r.max > 0)
        .sort((a, b) => Math.abs(a.max - needed) - Math.abs(b.max - needed));
      if (ranked.length) return ranked[0].kit;
    }
    return kits[0];
  }
  const fallback = gear.familyFallback[camera.type];
  return fallback || null;
}

/**
 * Renders the gear item as a real buy link when links are live, or as plain
 * non-clickable text (with a "link coming soon" note) until then. The
 * recommendation itself always shows — only the outbound link waits.
 */
function GearLink({ retailer, url, children }) {
  if (AFFILIATE.linksLive) {
    return (
      <a
        className="gear-link"
        href={affiliateUrl(retailer, url)}
        target="_blank"
        rel="sponsored noopener noreferrer"
      >
        {children}
      </a>
    );
  }
  return (
    <div className="gear-link gear-link-soon" aria-disabled="true">
      {children}
      <span className="gear-soon-tag">Buy link coming soon</span>
    </div>
  );
}

export function NdGear({ camera, settings }) {
  if (!AFFILIATE.enabled || !camera || !settings) return null;

  const needed = settings.ndNeeded && (settings.ndStatus === "recommended" || settings.ndStatus === "recommended-unsupported");
  if (!needed) return null;

  const kit = pickKit(camera, settings.ndFilter);
  if (!kit) return null;

  const short = settings.ndRequiredStops != null && settings.ndChosenStops != null
    ? Math.round((settings.ndRequiredStops - settings.ndChosenStops) * 10) / 10
    : null;

  return (
    <aside className="gear" aria-labelledby="gear-heading">
      <p className="eyebrow" id="gear-heading">
        <Icon name="nd" size={14} /> Filter for this shot
      </p>

      <p className="gear-reason">
        {settings.ndStatus === "recommended-unsupported" ? (
          <>
            This camera has no first-party ND option, and the exposure here needs one. A third-party
            filter is the difference between holding {settings.shutter} and raising shutter speed until
            motion goes harsh.
          </>
        ) : (
          <>
            The maths above calls for <strong className="data">{settings.ndFilter}</strong>
            {settings.ndRequiredStops != null && <> — about {settings.ndRequiredStops} stops</>}.
            {short != null && short > 0.5 && (
              <> The first-party set tops out below that, so this is where a stronger third-party kit earns its place.</>
            )}
          </>
        )}
      </p>

      {AFFILIATE.linksLive && <p className="gear-disclosure">{AFFILIATE.disclosure}</p>}

      <GearLink retailer={kit.retailer} url={kit.url}>
        <span className="gear-name">{kit.name}</span>
        {kit.covers && <span className="gear-covers data">{kit.covers.join(" · ")}</span>}
      </GearLink>
      {kit.note && <p className="gear-note">{kit.note}</p>}
    </aside>
  );
}

export function CardGear({ sdCard }) {
  if (!AFFILIATE.enabled || !sdCard || !sdCard.speedClass) return null;

  const cls = ["V90", "V60", "V30"].find((c) => sdCard.speedClass.includes(c));
  const item = cls ? gear.cards[cls] : null;
  if (!item) return null;

  return (
    <aside className="gear" aria-labelledby="card-gear-heading">
      <p className="eyebrow" id="card-gear-heading">
        <Icon name="card" size={14} /> Card for this mode
      </p>
      <p className="gear-reason">
        This recording mode needs <strong className="data">{cls}</strong> sustained writes. A slower
        card doesn't warn you — the clip stops partway through and you find out afterwards.
      </p>
      {AFFILIATE.linksLive && <p className="gear-disclosure">{AFFILIATE.disclosure}</p>}
      <GearLink retailer={item.retailer} url={item.url}>
        <span className="gear-name">{item.name}</span>
      </GearLink>
      {item.note && <p className="gear-note">{item.note}</p>}
    </aside>
  );
}
